import { useEffect } from 'react'

/**
 * Client-side page SEO for a JS-rendered SPA. Googlebot renders JavaScript,
 * so titles/descriptions/canonicals set here DO get indexed - but only if
 * every page sets them. index.html ships one static head (the umbrella
 * homepage's), so without this every route shares one snippet in search.
 *
 * Ownership split (keeps route data out of the entry bundle):
 *  - __root.tsx applies static per-path SEO (UMBRELLA_SEO) and skips paths
 *    under ROUTE_MANAGED_PREFIXES.
 *  - Code-split routes with dynamic data (states, scam library, articles,
 *    basics) call usePageSeo themselves. Route effects run before the root's
 *    on navigation, and the root skips their paths, so nothing clobbers.
 */
export type PageSeo = {
  title: string
  description?: string
  /** Canonical path on www.scamdefense.org. Defaults to the current path. */
  canonicalPath?: string
  noindex?: boolean
}

export function applyPageSeo(seo: PageSeo) {
  if (typeof document === 'undefined') return

  document.title = seo.title

  if (seo.description) {
    const desc = document.querySelector('meta[name="description"]')
    if (desc) desc.setAttribute('content', seo.description)
  }

  const path = seo.canonicalPath ?? window.location.pathname
  let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!canonical) {
    canonical = document.createElement('link')
    canonical.rel = 'canonical'
    document.head.appendChild(canonical)
  }
  canonical.href = `https://www.scamdefense.org${path === '/' ? '/' : path}`

  setRobotsNoindex(seo.noindex ?? false)
}

/** One managed robots meta: added for noindex pages, removed otherwise.
 *  index.html ships no robots meta, so absence means indexable. */
export function setRobotsNoindex(noindex: boolean) {
  if (typeof document === 'undefined') return
  const existing = document.querySelector('meta[name="robots"][data-managed]')
  if (noindex) {
    if (existing) return
    const meta = document.createElement('meta')
    meta.name = 'robots'
    meta.content = 'noindex, nofollow'
    meta.setAttribute('data-managed', 'true')
    document.head.appendChild(meta)
  } else if (existing) {
    existing.remove()
  }
}

export function usePageSeo(seo: PageSeo | null) {
  useEffect(() => {
    if (seo) applyPageSeo(seo)
  }, [seo?.title, seo?.description, seo?.canonicalPath, seo?.noindex])
}

/**
 * Swap the favicon set. index.html ships the ScamDefense icons; the
 * GranGuard face (incl. community-partner hosts like garvey.) shows the
 * GranGuard G shield instead (Lee, 2026-09-10). Sizes match index.html.
 */
export function applyFavicon(set: 'scamdefense' | 'granguard') {
  if (typeof document === 'undefined') return
  const prefix = set === 'granguard' ? '/granguard-' : '/'
  const bySize: Record<string, string> = {
    '32x32': `${prefix}favicon-32.png`,
    '16x16': `${prefix}favicon-16.png`,
  }
  document.querySelectorAll<HTMLLinkElement>('link[rel="icon"]').forEach(link => {
    const href = bySize[link.getAttribute('sizes') ?? '']
    if (href && link.getAttribute('href') !== href) link.setAttribute('href', href)
  })
  const apple = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]')
  const appleHref = `${prefix}apple-touch-icon.png`
  if (apple && apple.getAttribute('href') !== appleHref) apple.setAttribute('href', appleHref)
}
